import { useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import { userManagementApi } from "../../services/userManagementApi";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { Modal } from "../../components/common/Modal";
import { GapNotice } from "../../components/common/GapNotice";
import { AdminActionButton } from "../../components/common/AdminActionButton";
import { BanControls } from "../../components/common/BanControls";
import { useToast } from "../../context/ToastContext";

// Must match services/subscription.py _PLANS. The AI tier was retired in
// V047 (#234) -- AI ships later as a metered allowance inside these tiers,
// not as a tier of its own.
const TIERS = ["Free", "Harvester", "Business"];

/*
 * Must match what V128 seeds into app.ThemeFeatures, plus the three that
 * are not presets -- Basic resolves every flag to its default and Custom
 * writes nothing, so neither has a feature set to seed.
 *
 * This operator tool is the only picker that keeps its own list: the two
 * consumer pickers are sent the available themes with the theme payload,
 * which this app does not fetch. api/tests/unit/
 * test_theme_names_agree_everywhere.py holds this list to the seed, so
 * it cannot drift quietly -- it was `["Basic", "Advanced", "Custom"]`
 * until #485 and would simply have offered no preset at all.
 *
 * Order matches the consumer pickers: the open choices around the
 * presets, which rise by tier.
 */
const THEME_NAMES = [
  "Basic",
  "Hive Keeping",
  "Honey Selling",
  "Honey Business",
  "Inspection Ready",
  "Advanced",
  "Custom",
];

/** Search/detail/suspend/unsuspend/force-logout/tier-override all go
 * through userManagementApi against the real /management/users* routes in
 * api/functions/admin.py. Bulk invite, theme override, per-user risk score,
 * trusted-device management, and geofence override are still unbuilt on the
 * backend (see the GapNotice blocks below) and are left as-is. */
export function UserManagementPage() {
  const { notify } = useToast();
  const [query, setQuery] = useState("");
  const [includeSuspended, setIncludeSuspended] = useState(false);
  const [users, setUsers] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  // The live ban on the selected user, if any (#305). Fetched with the
  // detail rather than derived from is_deleted: a ban and a suspension are
  // different states and an account can carry either, both or neither.
  const [activeBan, setActiveBan] = useState(null);

  useEffect(() => {
    if (!selected) return setActiveBan(null);
    let cancelled = false;
    userManagementApi
      .listBans(selected.user_id)
      .then((r) => !cancelled && setActiveBan((r.bans || []).find((b) => b.is_active) || null))
      // Best effort: not knowing about a ban must not stop the rest of the
      // detail panel rendering.
      .catch(() => !cancelled && setActiveBan(null));
    return () => { cancelled = true; };
  }, [selected]);
  const [tierOverride, setTierOverride] = useState("");
  const [inviteRows, setInviteRows] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);

  // Provisioning a single account. Replaces the hand-written INSERT that
  // previously needed SQL access -- and therefore a firewall rule for
  // whatever IP the operator was on that day.
  // hive_limit blank means "use the plan default", which the API resolves
  // from services/subscription.py _PLANS. It used to default to a literal
  // 3; #186 raised the free cap to 5, so this screen kept handing out the
  // old allowance while the plan promised more -- found only when a real
  // account was created and came out short.
  const [createForm, setCreateForm] = useState({
    email: "", selected_theme: "Basic", subscription_tier: "Free", hive_limit: "",
  });
  const [creating, setCreating] = useState(false);
  const [createdNote, setCreatedNote] = useState(null);
  const fileInputRef = useRef(null);

  async function search() {
    setSearching(true);
    try {
      const r = await userManagementApi.list({
        search: query || undefined,
        include_suspended: includeSuspended ? "true" : undefined,
      });
      setUsers(r.users);
    } catch (err) {
      setUsers([]);
      notify(err.apiMessage || "Could not search users", "danger");
    } finally {
      setSearching(false);
    }
  }

  async function openUser(user) {
    try {
      const detail = await userManagementApi.get(user.user_id);
      setSelected(detail);
      setTierOverride(detail.subscription_tier);
    } catch (err) {
      if (err.response?.status === 404) {
        notify("That user no longer exists.", "danger");
        setUsers((prev) => prev?.filter((u) => u.user_id !== user.user_id) ?? prev);
      } else {
        notify(err.apiMessage || "Could not load user details", "danger");
      }
    }
  }

  function closeModal() {
    setSelected(null);
  }

  /** Applies a mutation's returned/target shape to both the open detail
   * modal and the underlying search row, and reports the error case
   * (self-suspend's 400 validation_error, a since-deleted user's 404)
   * as a toast rather than letting it reject unhandled. */
  async function runUserAction(userId, action, successMessage) {
    try {
      const result = await action();
      setSelected((prev) => (prev && prev.user_id === userId ? { ...prev, ...result } : prev));
      setUsers((prev) => (prev ? prev.map((u) => (u.user_id === userId ? { ...u, ...result } : u)) : prev));
      notify(successMessage(result), "success");
    } catch (err) {
      if (err.response?.status === 404) {
        notify("That user no longer exists.", "danger");
      } else if (err.apiErrorCode === "validation_error") {
        notify(err.apiMessage || "That action isn't allowed on this account", "danger");
      } else {
        notify(err.apiMessage || "Could not complete that action", "danger");
      }
    }
  }

  function handleCsvFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => setInviteRows(result.data),
    });
  }

  async function submitCreateUser() {
    const email = createForm.email.trim();
    if (!email) return notify("Enter the email address they will sign in with", "warning");
    setCreating(true);
    try {
      const r = await userManagementApi.create({
        email,
        selected_theme: createForm.selected_theme,
        subscription_tier: createForm.subscription_tier,
        // Omitted when blank, NOT coerced: Number("") is 0, which the API
        // rejects (ge=1). Sending nothing is what asks for the plan default.
        ...(createForm.hive_limit === "" ? {} : { hive_limit: Number(createForm.hive_limit) }),
      });
      // Kept on screen rather than shown as a toast that vanishes: the
      // account is INERT until an Entra identity exists for the same
      // address, and that is the step people forget. A disappearing
      // message is exactly the wrong medium for a required next action.
      setCreatedNote({ email: r.email, userId: r.user_id, nextStep: r.next_step });
      setCreateForm({ email: "", selected_theme: "Basic", subscription_tier: "Free", hive_limit: "" });
    } catch (err) {
      // 409 carries the existing user_id, so say which account it is
      // instead of a bare "already exists".
      const existingId = err.response?.data?.user_id;
      notify(
        existingId
          ? `${email} already has an account (user ${existingId}).`
          : err.apiMessage || "Could not create the account",
        "danger",
      );
    } finally {
      setCreating(false);
    }
  }

  async function submitBulkInvite() {
    try {
      const r = await userManagementApi.bulkInvite(inviteRows);
      setInviteResult(r);
    } catch (err) {
      setInviteResult({ error: err.apiMessage || "Bulk invite isn't wired up on the backend yet -- see userManagementApi.js" });
    }
  }

  return (
    <div>
      <h1>User Management</h1>

      <Card title="Add a User" className="section-spacing">
        <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
          Creates the Bee Keeper account. They also need an identity in the Entra External ID
          tenant using this <strong>exact</strong> email address before they can sign in.
        </p>
        <div style={{ display: "grid", gap: "var(--space-3)", maxWidth: 520 }}>
          <label style={{ display: "grid", gap: 4 }}>
            <span>Email address</span>
            <input
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="newtester@example.com"
              className="touch-target"
            />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>Theme</span>
            <select
              value={createForm.selected_theme}
              onChange={(e) => setCreateForm((f) => ({ ...f, selected_theme: e.target.value }))}
              className="touch-target"
            >
              {THEME_NAMES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>Subscription tier</span>
            <select
              value={createForm.subscription_tier}
              onChange={(e) => setCreateForm((f) => ({ ...f, subscription_tier: e.target.value }))}
              className="touch-target"
            >
              {TIERS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>Hive limit</span>
            <input
              type="number"
              min="1"
              max="1000"
              placeholder="Leave blank for the plan default"
              value={createForm.hive_limit}
              onChange={(e) => setCreateForm((f) => ({ ...f, hive_limit: e.target.value }))}
              className="touch-target"
            />
          </label>
          <div>
            <Button onClick={submitCreateUser} disabled={creating}>
              {creating ? "Creating…" : "Create Account"}
            </Button>
          </div>
        </div>

        {createdNote ? (
          <div
            role="status"
            style={{
              marginTop: "var(--space-3)",
              padding: "var(--space-3)",
              border: "1px solid var(--color-border)",
              borderLeft: "4px solid var(--color-success)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <strong>Account created for {createdNote.email}</strong> (user {createdNote.userId}).
            <p style={{ marginBottom: 0 }}>{createdNote.nextStep}</p>
          </div>
        ) : null}
      </Card>

      <Card title="Bulk User Invite (CSV)" className="section-spacing">
        <p style={{ color: "var(--color-text-muted)" }}>CSV columns: email, initial_theme (optional), initial_tier (optional)</p>
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvFile} />
        {inviteRows && (
          <div style={{ marginTop: "var(--space-3)" }}>
            <p>{inviteRows.length} rows parsed.</p>
            <Button onClick={submitBulkInvite}>Send Invites</Button>
          </div>
        )}
        {inviteResult && (
          <p style={{ color: inviteResult.error ? "var(--color-danger)" : "var(--color-success)", marginTop: "var(--space-2)" }}>
            {inviteResult.error || `${inviteResult.invited} invited, ${inviteResult.failed || 0} failed.`}
          </p>
        )}
      </Card>

      <Card title="Search Users" className="section-spacing" actions={<Button size="sm" onClick={search} disabled={searching}>{searching ? "Searching…" : "Search"}</Button>}>
        <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search by email…"
            className="touch-target"
            style={{ width: "100%", maxWidth: 360 }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <input type="checkbox" checked={includeSuspended} onChange={(e) => setIncludeSuspended(e.target.checked)} />
            Include suspended
          </label>
        </div>
        <div style={{ marginTop: "var(--space-4)" }}>
          <DataTable
            rows={users}
            rowKey="user_id"
            loading={searching}
            emptyTitle={users === null ? "Search to see users" : "No users found"}
            onRowClick={openUser}
            columns={[
              { key: "email", header: "Email" },
              { key: "selected_theme", header: "Theme", render: (r) => <Badge tone="honey">{r.selected_theme}</Badge> },
              { key: "subscription_tier", header: "Tier" },
              { key: "last_active", header: "Last Active", render: (r) => (r.last_active ? new Date(r.last_active).toLocaleString() : "--") },
              { key: "is_deleted", header: "Status", render: (r) => <Badge tone={r.is_deleted ? "danger" : "success"}>{r.is_deleted ? "Suspended" : "Active"}</Badge> },
            ]}
          />
        </div>
      </Card>

      <Modal open={!!selected} onClose={closeModal} title={selected?.email || "User"} size="lg">
        {selected && (
          <div style={{ display: "grid", gap: "var(--space-4)" }}>
            <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
              <Badge tone="honey">{selected.selected_theme}</Badge>
              <Badge tone="info">{selected.subscription_tier}</Badge>
              <Badge tone={selected.is_deleted ? "danger" : "success"}>{selected.is_deleted ? "Suspended" : "Active"}</Badge>
            </div>

            <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap", color: "var(--color-text-muted)" }}>
              <span>Hives: {selected.active_hive_count ?? "--"}</span>
              <span>Active sessions: {selected.active_session_count ?? "--"}</span>
              <span>Home region: {selected.home_region || "--"}</span>
              <span>Joined: {selected.created_date ? new Date(selected.created_date).toLocaleDateString() : "--"}</span>
            </div>

            {selected.latest_jit_request && (
              <p style={{ color: "var(--color-text-muted)" }}>
                Latest JIT access request: <Badge tone={selected.latest_jit_request.status === "Approved" ? "success" : selected.latest_jit_request.status === "Denied" ? "danger" : "warning"}>{selected.latest_jit_request.status}</Badge>
                {selected.latest_jit_request.expires_at && ` (expires ${new Date(selected.latest_jit_request.expires_at).toLocaleString()})`}
              </p>
            )}

            <div>
              <h3>Overrides</h3>
              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
                <select className="touch-target" value={tierOverride} onChange={(e) => setTierOverride(e.target.value)}>
                  {TIERS.map((t) => <option key={t}>{t}</option>)}
                </select>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    runUserAction(
                      selected.user_id,
                      () => userManagementApi.overrideTier(selected.user_id, tierOverride),
                      (r) => `Tier changed from ${r.old_tier} to ${r.new_tier}.`
                    )
                  }
                >
                  Force Tier Change
                </Button>
              </div>
              <div style={{ marginTop: "var(--space-3)" }}>
                <GapNotice>
                  Forcing a theme change (PUT /management/users/{"{id}"}/theme) isn't built yet -- only the tier override
                  route above exists today.
                </GapNotice>
              </div>
            </div>

            <div>
              <h3>Session &amp; Device Controls</h3>
              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
                <AdminActionButton
                  label="Force Logout"
                  danger={false}
                  confirmBody={<p>This will end all of {selected.email}'s active sessions immediately.</p>}
                  onConfirm={() =>
                    runUserAction(
                      selected.user_id,
                      () => userManagementApi.forceLogout(selected.user_id),
                      (r) => `${r.sessions_revoked} session(s) revoked.`
                    )
                  }
                />
                {selected.is_deleted ? (
                  <AdminActionButton
                    label="Reinstate Account"
                    danger={false}
                    confirmBody={<p>{selected.email} will be able to sign in again.</p>}
                    onConfirm={() =>
                      runUserAction(
                        selected.user_id,
                        () => userManagementApi.unsuspend(selected.user_id),
                        () => "Account reinstated."
                      )
                    }
                  />
                ) : (
                  <AdminActionButton
                    label="Suspend Account"
                    confirmBody={<p>{selected.email} will be unable to sign in until reinstated. This is logged to the audit trail.</p>}
                    onConfirm={() =>
                      runUserAction(
                        selected.user_id,
                        () => userManagementApi.suspend(selected.user_id),
                        () => "Account suspended."
                      )
                    }
                  />
                )}
              </div>

              {/* Ban is NOT suspend, and the two sit together so that is
                  visible: suspend removes the account, a ban blocks the
                  person and keeps it. See #305. */}
              <div style={{ marginTop: "var(--space-3)" }}>
                <h3>Ban from signing in</h3>
                <BanControls
                  user={selected}
                  activeBan={activeBan}
                  onBan={(reason, days) =>
                    runUserAction(
                      selected.user_id,
                      () => userManagementApi.ban(selected.user_id, reason, days),
                      (r) => (r.days ? `Banned for ${r.days} days.` : "Banned permanently."),
                    )
                  }
                  onUnban={() =>
                    runUserAction(
                      selected.user_id,
                      () => userManagementApi.unban(selected.user_id),
                      () => "Ban lifted.",
                    )
                  }
                />
              </div>
            </div>

            <div>
              <h3>Trusted Devices</h3>
              <GapNotice>GET /management/users/{"{id}"}/trusted-devices would list app.TrustedDevices rows for this user -- not built yet.</GapNotice>
            </div>

            <div>
              <h3>Geofence Override</h3>
              <GapNotice>Temporary bypass with a required reason -- would extend app.Users.geofencing_travel_mode's existing JSON shape rather than a new column.</GapNotice>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
